import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Fade,
  Stack,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  Add as AddIcon,
  SwapHoriz as TransferIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import { postManyEntriesWithReconciliation, putManyEntry } from '../../services/api/entriesService';
import { mutate } from 'swr';
import { toastNotification } from '../../shared/toastNotification';

// Custom hooks
import { useCategoriesData } from './hooks/useCategoriesData';
import { useBankAccountsData } from './hooks/useBankAccountsData';
import { useVitaUserData } from './hooks/useVitaUserData';
import { useFormState } from './hooks/useFormState';
import { useFilteredCategories } from './hooks/useFilteredCategories';

// Utils
import { validateForm } from './utils/validationUtils';
import { createEntriesData } from './utils/entryCreationUtils';

// Components
import TransactionDisplay from './components/TransactionDisplay';
import FormSection from './components/FormSection';
import AutoInfoSection from './components/AutoInfoSection';
const CreateEntryFromTransactionDialog = ({
  open,
  onClose,
  transaction,
  transactions,
  bulkEditPairs,
  onBulkEditConfirm,
  selectedStatement,
  refreshAllData,
  clearSelections,
}) => {
  const companyId = localStorage.getItem('companyId');
  const userId = localStorage.getItem('userId');

  const [isLoading, setIsLoading] = useState(false);

  const isBulkEditMode = Array.isArray(bulkEditPairs) && bulkEditPairs.length > 0;

  // Determinar se estamos trabalhando com uma ou múltiplas transações
  const isBulkMode = (transactions && transactions.length > 1) || isBulkEditMode;

  // Custom hooks
  const { categories: categoriesArray, isLoadingCategories } = useCategoriesData(companyId, open);
  const { availableBankAccounts, isLoadingBanks } = useBankAccountsData(
    companyId,
    selectedStatement
  );
  const { vitaUserId, isLoadingVitaUser } = useVitaUserData(userId);
  const {
    entryType,
    setEntryType,
    formData,
    setFormData,
    errors,
    setErrors,
    handleInputChange,
    allPositive,
    allNegative,
    currentTransactions,
  } = useFormState(transaction, transactions, open, isBulkMode);

  useEffect(() => {
    if (isBulkEditMode && entryType !== 'regular') {
      setEntryType('regular');
    }
  }, [isBulkEditMode, entryType, setEntryType]);

  // Filtered categories hook
  const filteredCategories = useFilteredCategories(
    categoriesArray,
    allPositive,
    allNegative,
    entryType,
    formData,
    setFormData
  );

  const handleSubmit = async () => {
    const newErrors = validateForm(
      entryType,
      formData,
      currentTransactions,
      categoriesArray,
      allPositive,
      allNegative,
      isBulkMode
    );
    if (!isBulkEditMode) {
      setErrors(newErrors);

      if (Object.keys(newErrors).length > 0) {
        return;
      }
    }

    if (!currentTransactions.length || (!selectedStatement && !isBulkEditMode)) {
      toastNotification('error', 'Dados das transações não encontrados');
      return;
    }

    if (!vitaUserId) {
      toastNotification('error', 'Dados do usuário não encontrados. Tente fazer login novamente.');
      return;
    }

    setIsLoading(true);

    try {
      if (isBulkEditMode) {
        const updates = [];

        bulkEditPairs.forEach((pair) => {
          const txn = pair.transaction;
          const entry = pair.journalEntry;
          if (!entry) return;

          const formDesc =
            formData.individualDescriptions[txn?.transactionHash] ||
            formData.description.trim();
          const defaultBankDesc = txn?.description || 'Lançamento criado a partir do extrato';
          const userModifiedDesc = formDesc && formDesc !== defaultBankDesc;
          // Only override entry description if user explicitly changed it in the form,
          // or if the entry has no description (fill from bank statement as fallback)
          const description = userModifiedDesc
            ? formDesc
            : entry.description || formDesc;
          const categoryId = formData.categoryId || entry.categoryId;

          if (description !== entry.description || categoryId !== entry.categoryId) {
            updates.push({
              id: entry.id,
              description,
              categoryId,
              userUpdateId: vitaUserId,
            });
          }
        });

        if (updates.length > 0) {
          await putManyEntry(updates);
        }

        if (onBulkEditConfirm) {
          await onBulkEditConfirm(bulkEditPairs);
        }
      } else {
        const { entriesData, reconciliationPairs } = createEntriesData(
          currentTransactions,
          entryType,
          formData,
          categoriesArray,
          selectedStatement,
          companyId,
          vitaUserId
        );

        // Criar todos os lançamentos em massa e conciliá-los automaticamente
        await postManyEntriesWithReconciliation(entriesData, reconciliationPairs, {
          statementId: selectedStatement.id,
          bankAccountId: selectedStatement.bankAccountId,
          notes: `Lançamentos criados automaticamente a partir de transações do extrato`,
        });
      }

      const message = isBulkEditMode
        ? `${currentTransactions.length} lançamentos atualizados e conciliados com sucesso!`
        : isBulkMode
        ? `${currentTransactions.length} lançamentos ${
            entryType === 'transfer' ? 'de transferência' : ''
          } criados e conciliados com sucesso!`
        : `Lançamento ${
            entryType === 'transfer' ? 'de transferência' : ''
          } criado e conciliado com sucesso!`;

      if (!isBulkEditMode) {
        toastNotification('success', message);
      }

      // Clear selections after successful creation
      if (clearSelections) {
        clearSelections();
      }

      // Fechar modal e atualizar dados
      onClose();

      // Atualizar dados usando a função consistente do hook pai
      if (refreshAllData) {
        await refreshAllData();
      } else {
        // Fallback: invalidar caches manualmente se refreshAllData não estiver disponível
        mutate('entriesReconciliation');
        mutate([`reconciliation-new-transactions`, selectedStatement.id]);
        mutate([`reconciliation-new-stats`, companyId]);
        mutate([`reconciliation-new-statements`, companyId]);
      }

    } catch (error) {
      console.error('Erro ao criar lançamento:', error);
      const errorMessage =
        error.response?.data?.message || error.message || 'Erro ao criar lançamento';
      toastNotification('error', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setFormData({
        categoryId: '',
        description: '',
        individualDescriptions: {},
        targetBankAccountId: '',
      });
      setEntryType('regular');
      setErrors({});
      onClose();
    }
  };

  if (!currentTransactions.length) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          minHeight: isBulkMode ? 700 : 600,
          background: 'linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%)',
        },
      }}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 400 }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          py: 2.5,
          px: 3,
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              borderRadius: 2,
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AddIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="600">
              {isBulkMode
                ? `Conciliar ${currentTransactions.length} Lançamentos`
                : 'Conciliar Lançamento Correspondente'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {isBulkMode
                ? 'Conciliação em massa com conciliação automática'
                : 'Conciliar lançamento com conciliação automática'}
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Este assistente ajuda você a conciliar lançamentos no Vita correspondentes aos extratos bancários selecionados, reconciliando-os automaticamente.">
          <IconButton size="small" sx={{ color: 'white' }}>
            <HelpIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pb: 2 }}>
        <Stack spacing={3}>
          <TransactionDisplay
            isBulkMode={isBulkMode}
            currentTransactions={currentTransactions}
            selectedStatement={selectedStatement}
          />

          <FormSection
            isBulkMode={isBulkMode}
            isBulkEditMode={isBulkEditMode}
            entryType={entryType}
            setEntryType={setEntryType}
            formData={formData}
            handleInputChange={handleInputChange}
            errors={errors}
            allPositive={allPositive}
            filteredCategories={filteredCategories}
            isLoadingCategories={isLoadingCategories}
            availableBankAccounts={availableBankAccounts}
            isLoadingBanks={isLoadingBanks}
            currentTransactions={currentTransactions}
          />

          <AutoInfoSection
            isBulkMode={isBulkMode}
            currentTransactions={currentTransactions}
            entryType={entryType}
            allPositive={allPositive}
            formData={formData}
            availableBankAccounts={availableBankAccounts}
            selectedStatement={selectedStatement}
          />
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{ p: 3, pt: 0, background: 'linear-gradient(to top, #f8f9fa 0%, transparent 100%)' }}
      >
        <Button
          onClick={handleClose}
          disabled={isLoading}
          variant="outlined"
          size="large"
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
          }}
        >
          Cancelar
        </Button>
        <LoadingButton
          onClick={handleSubmit}
          loading={isLoading || isLoadingVitaUser}
          variant="contained"
          size="large"
          startIcon={entryType === 'transfer' ? <TransferIcon /> : <AddIcon />}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5568d3 0%, #654a8b 100%)',
            },
          }}
        >
          {isBulkMode
            ? `Conciliar ${currentTransactions.length} ${
                entryType === 'transfer' ? 'Transferências' : 'Lançamentos'
              }`
            : `Conciliar ${entryType === 'transfer' ? 'Transferência' : 'Lançamento'}`}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default CreateEntryFromTransactionDialog;
